import type { PlannedRow } from './costDraft';

export interface CreateCatalogComponentInput {
  id: string; vendor: string; effectiveFrom: string; componentSku: string;
  kind: string; value: string; recordedAt: string; actor: string;
  version: number; supersedes: string | null;
}

export function buildCreateInputs(
  planned: PlannedRow[], vendor: string, actor: string, now: string,
): CreateCatalogComponentInput[] {
  return planned.map((p) => {
    const version = p.previousVersion + 1;
    return {
      id: `${vendor}#${p.effectiveFrom}#${p.componentSku}#v${String(version).padStart(4, '0')}`,
      vendor,
      effectiveFrom: p.effectiveFrom,
      componentSku: p.componentSku,
      kind: p.kind,
      value: p.value,
      recordedAt: now,
      actor,
      version,
      // NOT accompanied by an update of the predecessor. CatalogComponent has no
      // `update` in its auth surface — immutability is the point — and resolveAt
      // selects on max(effectiveFrom <= quarter) then max(version). Nothing needs
      // flipping, because nothing here is a claim about what came after.
      //
      // supersedes points BACKWARD, at the row this one replaced. That is true
      // the moment it is written and stays true forever, which is exactly why it
      // survived the Task 3 cleanup that deleted isLatest. Null when this is the
      // first version at the tuple — latestAt found no predecessor.
      supersedes: p.previousId,
    };
  });
}

export interface StaleTarget {
  componentSku: string; effectiveFrom: string;
  reviewedVersion: number; currentVersion: number;
}

/**
 * Makes a concurrent author VISIBLE rather than preventing them. Two people
 * committing at once would otherwise both compute version+1 and one would
 * silently lose. Full conditional writes are the eventual answer; a visible
 * abort is cheaper and strictly better than today's silent loss.
 */
export function detectStaleness(
  planned: PlannedRow[],
  current: { componentSku: string; effectiveFrom: string; version: number }[],
): StaleTarget[] {
  const out: StaleTarget[] = [];
  for (const p of planned) {
    const latest = current
      .filter((r) => r.componentSku === p.componentSku && r.effectiveFrom === p.effectiveFrom)
      .reduce((max, r) => Math.max(max, r.version), 0);
    if (latest !== p.previousVersion) {
      out.push({ componentSku: p.componentSku, effectiveFrom: p.effectiveFrom,
                 reviewedVersion: p.previousVersion, currentVersion: latest });
    }
  }
  return out;
}
